/**
 * AntiAliasingEffect - unified anti-aliasing for WebGL (SMAA) and WebGPU (FXAA).
 */

import { SMAAEffect, SMAAPreset } from 'postprocessing';
import { fxaa } from 'three/addons/tsl/display/FXAANode.js';

import { type EffectContext, IPostProcessEffect, type WebGLEffectData, type WebGPUComposeContext, type WebGPUEffectNode } from '../IPostProcessEffect.js';
import { type AntiAliasingEffectConfig, AntiAliasingPreset, DefaultPostprocessEffectConfigs, PostProcessPass, PostProcessRenderOrder } from '../PostProcessConfig.js';


/** Map our preset to postprocessing library preset */
const PRESET_MAP: Record<number, SMAAPreset> = {
  [AntiAliasingPreset.LOW]: SMAAPreset.LOW,
  [AntiAliasingPreset.MEDIUM]: SMAAPreset.MEDIUM,
  [AntiAliasingPreset.HIGH]: SMAAPreset.HIGH,
  [AntiAliasingPreset.ULTRA]: SMAAPreset.ULTRA,
};

/**
 * Unified Anti-Aliasing effect for both WebGL and WebGPU.
 *
 * WebGL: Uses SMAA (Subpixel Morphological Anti-Aliasing)
 * WebGPU: Uses FXAA (Fast Approximate Anti-Aliasing)
 */
export class AntiAliasingEffect extends IPostProcessEffect<AntiAliasingEffectConfig> {
  override readonly type = PostProcessPass.AntiAliasing;
  override readonly order = PostProcessRenderOrder.AntiAliasing;

  override getDefaultConfig(): AntiAliasingEffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.AntiAliasing] };
  }

  // ============= WebGL Implementation =============

  override createWebGLEffect(config: AntiAliasingEffectConfig, _context: EffectContext): WebGLEffectData {
    const defaults = this.getDefaultConfig();
    const preset = config.preset ?? defaults.preset!;

    const effect = new SMAAEffect({
      preset: PRESET_MAP[preset] ?? SMAAPreset.HIGH,
    });

    return { effects: [effect] };
  }

  override applyWebGLConfig(effectData: WebGLEffectData, config: AntiAliasingEffectConfig): void {
    const smaaEffect = effectData.effects[0] as SMAAEffect;

    if (smaaEffect && config.preset !== undefined) {
      const preset = PRESET_MAP[config.preset] ?? SMAAPreset.HIGH;
      smaaEffect.applyPreset(preset);
    }
  }

  // ============= WebGPU Implementation =============

  override createWebGPUNode(_config: AntiAliasingEffectConfig, _context: EffectContext): WebGPUEffectNode {
    return {
      node: null as any, // Not used directly
      compose: (outputNode: any, _ctx: WebGPUComposeContext) => {
        // Replace output with anti-aliased version
        return fxaa(outputNode);
      },
    };
  }

  override applyWebGPUConfig(_node: WebGPUEffectNode, _config: AntiAliasingEffectConfig): void {
    // FXAA has no configurable parameters
  }

  override requiresRebuild(oldConfig: AntiAliasingEffectConfig, newConfig: AntiAliasingEffectConfig): boolean {
    // Only rebuild if enabled state changes
    return oldConfig.enabled !== newConfig.enabled;
  }
}
